import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";

import { HttpResponsesHandlersFactory } from "@bbwt/modules/data-service";
import { IPagedData, PagedCrudService } from "@features/grid";
import { IRole } from "./role";
import { IHttpResponseHandlerSettings } from "@bbwt/modules/data-service/http-responses-handler";
import { ApiAccessModel } from "./api-access-model";
import { AppStorage } from "@bbwt/utils/app-storage";
import { IQueryCommand } from "@features/filter";
import { GoverningBodyService } from "@project/governingbody/governingbody.service";
import { AppOnlineStateService } from "@main/pwa/app-online-state.service";
import { LinkUserService, PatientLogin, UserLogin } from "@main/users";
import { AccountService } from "@account/services";
import { IOrganization, OrganizationService } from "@main/companies";
import { BroadcastService } from "@bbwt/modules/broadcasting";


@Injectable({
    providedIn: "root"
})
export class RoleService extends PagedCrudService<IRole> {
    readonly url = "api/role";
    readonly entityTitle = "Role";

    private readonly currentOrganizationRolesKey = "current-organization-roles";

    governingBodyId: string;

    private _currentOrganizationRoles: IRole[] = AppStorage.getItem<IRole[]>(this.currentOrganizationRolesKey);

    constructor(
        http: HttpClient,
         handlersFactory: HttpResponsesHandlersFactory,
         private broadcastService: BroadcastService,
         private governingBodyService: GoverningBodyService,
         private appOnlineStateService: AppOnlineStateService
         ) {
        super(http, handlersFactory);

        broadcastService.on<UserLogin | PatientLogin>(AccountService.UserLoginEventName).subscribe(async  _ => {
            await this.refreshCurrentOrganizationRoles();
        });

        broadcastService.on(AccountService.UserLogoutEventName).subscribe(() => {
            this.setCurrentOrganizationRoles(null);

        });

        broadcastService.on<IOrganization>(OrganizationService.OrganizationChangedEventName).subscribe(async _ => {
            await this.refreshCurrentOrganizationRoles();
        });

        broadcastService.on<IOrganization>(LinkUserService.UserSelectedOrganizationChangedEventName).subscribe(async _ => {
            await this.refreshCurrentOrganizationRoles();
        });
    }

    get apiAccessModel(): ApiAccessModel {
        return AppStorage.getItem<ApiAccessModel>("api-access-model");
    }

    set apiAccessModel(val: ApiAccessModel) {
        AppStorage.setItem("api-access-model", val);
    }

    get isPermissionBasedModel(): boolean {
        return this.apiAccessModel == ApiAccessModel.PermissionBased;
    }

    get currentOrganizationRoles(): IRole[] {
        return this._currentOrganizationRoles;
    }

    async initialize(): Promise<void> {
        this.apiAccessModel = ApiAccessModel.PermissionBased;

        this._currentOrganizationRoles = AppStorage.getItem<IRole[]>(this.currentOrganizationRolesKey);

        if (!this._currentOrganizationRoles) {
            await this.refreshCurrentOrganizationRoles();
        }

        try {
            const model = await this.getApiAccessModel();
            this.apiAccessModel = model ? model : ApiAccessModel.PermissionBased;
        } catch {
            // Left blank intentionally
        }
    }

    getCurrentOrganizationRoles(responseHandlerSettings?: IHttpResponseHandlerSettings): Promise<IRole[]> {
        return this.handleRequest<IRole[]>(
            this.http.get<IRole[]>(`${this.url}/current-organization`),
            this.handlersFactory.getDefault(responseHandlerSettings)
        );
    }

    getOrganizationRoles(organizationId: string, responseHandlerSettings?: IHttpResponseHandlerSettings): Promise<IRole[]> {
        return this.handleRequest<IRole[]>(
            this.http.get<IRole[]>(`${this.url}/organization/${organizationId}`),
            this.handlersFactory.getDefault(responseHandlerSettings)
        );
    }

    getGoverningBodyRolesPage(queryCommand: IQueryCommand, responseHandlerSettings?: IHttpResponseHandlerSettings): Promise<IPagedData<IRole>> {
        if (!this.governingBodyId) {
            Promise.resolve(<IPagedData<IRole>>{ items: [], total: 0 });
            return;
        }
        return this.governingBodyService.getRolesPage(this.governingBodyId, queryCommand, responseHandlerSettings);
    }

    getGoverningBodyRoles(): Promise<IRole[]> {
        return this.governingBodyService.getRoles(this.governingBodyId);
    }

    createRole(data: IRole, responseHandlerSettings?: IHttpResponseHandlerSettings): Promise<IRole> {
        return this.governingBodyService.createRole(this.governingBodyId, data, responseHandlerSettings);
    }

    getApiAccessModel(responseHandlerSettings?: IHttpResponseHandlerSettings): Promise<ApiAccessModel> {
        return this.handleRequest<ApiAccessModel>(
            this.http.get<ApiAccessModel>(`${this.url}/model`),
            this.handlersFactory.getDefault(responseHandlerSettings)
        );
    }

    isDeletable(roleId: string): Promise<boolean> {
        return this.handleRequest<boolean>(
            this.http.get<boolean>(`${this.url}/${roleId}/is-deletable`),
            this.handlersFactory.getDefault()
        );
    }

    refreshCurrentOrganizationRoles(responseHandlerSettings?: IHttpResponseHandlerSettings): Promise<IRole[]> {
        return this.getCurrentOrganizationRoles(responseHandlerSettings).then(roles => {
            this.setCurrentOrganizationRoles(roles);
            return roles;
        });
    }

    private setCurrentOrganizationRoles(roles: IRole[]): void {
        this._currentOrganizationRoles = roles;
        AppStorage.setItem(this.currentOrganizationRolesKey, this._currentOrganizationRoles);
    }
}